import {
  ComponentProps,
  forwardRef,
  ReactNode,
  useCallback,
  useMemo,
  useState,
} from 'react';
import { useTranslation } from '@sib/i18n';
import {
  BaseSelect,
  cn,
  Loader,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@sib/ui';
import { Skeleton } from '../../components/Skeleton';

export interface SelectOption {
  label: string | ReactNode;
  Icon?: ReactNode;
  triggerLabel?: string | ReactNode;
  value: string | number | null;
  disabled?: boolean;
  hidden?: boolean;
}

export type SelectProps = ComponentProps<typeof BaseSelect> & {
  options: SelectOption[];
  /** Options used only to resolve the trigger label when `value` is not in `options`. */
  triggerOptions?: SelectOption[];
  placeholder?: string;
  size?: ComponentProps<typeof SelectTrigger>['size'];
  selectTriggerProps?: ComponentProps<typeof SelectTrigger>;
  selectValueProps?: ComponentProps<typeof SelectValue>;
  selectContentProps?: ComponentProps<typeof SelectContent>;
  selectItemProps?: Omit<ComponentProps<typeof SelectItem>, 'value'>;
  loading?: boolean;
  /** Shows skeleton rows in the list while open; does not disable the trigger. */
  contentLoading?: boolean;
  contentLoadingSkeletonCount?: number;
  clearable?: boolean;
  onClear?: () => void;
  onItemSelect?: (value: string, index: number) => void;
  emptyMessage?: string;
};

const Select = forwardRef<HTMLSpanElement, SelectProps>(
  (
    {
      options,
      triggerOptions,
      selectContentProps,
      selectItemProps,
      selectTriggerProps,
      selectValueProps,
      placeholder,
      size,
      loading,
      contentLoading,
      contentLoadingSkeletonCount = 3,
      clearable,
      onClear,
      onItemSelect,
      emptyMessage,
      ...props
    },
    ref
  ) => {
    const { t } = useTranslation();
    const [open, setOpen] = useState(props.open);

    const selectableOptions = useMemo(
      () => options.filter(option => option.value !== null),
      [options]
    );

    const selectedValue = useMemo(() => {
      const selected =
        selectableOptions.find(({ value }) => value === props.value) ??
        (triggerOptions ?? []).find(({ value }) => value === props.value);

      if (!selected) return null;

      const value = selected?.triggerLabel || selected?.label || null;

      return (
        <span className="inline-flex max-w-full min-w-0 flex-1 items-center gap-2 overflow-hidden">
          {selected?.Icon}
          <span className="min-w-0 flex-1 truncate">{value}</span>
        </span>
      );
    }, [selectableOptions, props.value, triggerOptions]);

    const isOpenState = props.open ?? open;
    const emptyLabel = emptyMessage ?? t('noResults');

    const handleItemPointerDown = useCallback(
      (e: React.PointerEvent) => {
        e.preventDefault();
        e.stopPropagation();
        if (props.open === undefined) setOpen(false);

        if (onClear) return onClear();

        props.onValueChange?.('');
      },
      [onClear, props]
    );

    return (
      <BaseSelect
        {...props}
        value={props.value == null ? '' : String(props.value)}
        onValueChange={val => {
          if (!val) return;

          props.onValueChange?.(val);
        }}
        {...(props.open === undefined && {
          open,
          onOpenChange: value => {
            setOpen(value);
            props.onOpenChange?.(value);
          },
        })}
      >
        <SelectTrigger
          {...selectTriggerProps}
          size={selectTriggerProps?.size ?? size}
          className={cn(
            selectTriggerProps?.className,
            '[&>span]:w-[calc(100%-16px)] [&>span]:text-left'
          )}
          disabled={props.disabled || loading}
          {...(loading && {
            icon: <Loader />,
          })}
        >
          <SelectValue
            ref={ref}
            placeholder={placeholder}
            {...selectValueProps}
          >
            {selectedValue}
            {selectTriggerProps?.children}
          </SelectValue>
        </SelectTrigger>
        {isOpenState && (
          <SelectContent {...selectContentProps}>
            {contentLoading && selectableOptions.length === 0 && (
              <div className="flex flex-col gap-1 p-1">
                {Array.from({ length: contentLoadingSkeletonCount }).map(
                  (_, index) => (
                    <Skeleton
                      key={`select-content-skeleton-${index}`}
                      className="h-8 w-full"
                    />
                  )
                )}
              </div>
            )}
            {!contentLoading && selectableOptions.length === 0 && (
              <div className="text-muted-foreground px-2 py-3 text-center text-sm">
                {emptyLabel}
              </div>
            )}
            {selectableOptions.map(
              ({ value: rawValue, label, disabled, hidden, Icon }, index) => {
                const isSelected = rawValue === props.value;
                const value = String(rawValue);

                return (
                  <SelectItem
                    {...selectItemProps}
                    value={value}
                    key={`select-item-${index}-${value}`}
                    className={cn(
                      disabled && 'text-muted-foreground pointer-events-none',
                      hidden && 'hidden',
                      selectItemProps?.className
                    )}
                    {...(clearable &&
                      isSelected && {
                        onPointerDown: handleItemPointerDown,
                      })}
                    {...(onItemSelect && {
                      onPointerDown: (e: React.PointerEvent) => {
                        e.preventDefault();
                        e.stopPropagation();
                        onItemSelect(value, index);
                      },
                    })}
                  >
                    <div className="flex max-w-full min-w-0 flex-1 flex-row items-center gap-2 overflow-hidden">
                      {Icon}
                      <span className="min-w-0 flex-1 overflow-hidden">
                        {label}
                      </span>
                    </div>
                  </SelectItem>
                );
              }
            )}
            {selectContentProps?.children}
          </SelectContent>
        )}
      </BaseSelect>
    );
  }
);

export { Select };
